import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_ENDPOINTS } from "../../api/config";
import EmpLayout from "./emp_layout";
import './css/view_applicants.css';

const FILTERS = ["All", "New", "Shortlisted", "Rejected"];
const pillMap = { new: "pill-new", shortlisted: "pill-shortlisted", rejected: "pill-rejected" };

export function ViewApplicants() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [jobDetail, setJobDetail] = useState(null);
  const [candidates, setCands] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API_ENDPOINTS.LISTINGS}/${listingId}/applicants`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setJobDetail(data.job);
        setCands(data.applicants);
      }
    } catch (err) {
      console.error("Error fetching applicants", err);
    } finally {
      setLoading(false);
    }
  }, [listingId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const startEdit = () => {
    setEditForm({
      title: jobDetail.title,
      city: jobDetail.city,
      jobType: jobDetail.jobType
    });
    setIsEditing(true);
  };

  const saveJobUpdate = async () => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_ENDPOINTS.LISTINGS}/${listingId}`, {
      method: "PUT",
      headers: { 
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}` 
      },
      body: JSON.stringify(editForm)
    });
    if (res.ok) {
      setJobDetail({ ...jobDetail, ...editForm });
      setIsEditing(false);
    }
  };

  const updateStatus = async (appId, newStatus) => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API_ENDPOINTS.APPLICATIONS}/${appId}/status`, {
        method: "PATCH",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}` 
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        setCands(prev => prev.map(c => c.id === appId ? { ...c, status: newStatus } : c));
      }
    } catch (err) {
      console.error("Failed to update status", err);
    }
  };

  const filtered = filter === "All" 
    ? candidates 
    : candidates.filter(c => c.status.toLowerCase() === filter.toLowerCase());

  if (loading) return <EmpLayout title="Loading..."></EmpLayout>;

  return (
    <EmpLayout title="View" titleEm="Applicants">
      {/* Header Info from DB */}
      <div className="va-hdr">
        <div className="va-hdr-icon">✂️</div>
        <div className="va-hdr-info">
          {
            isEditing ? (
              <div className="va-edit-fields">
                <input className="emp-input" value={editForm.title} onChange={e => setEditForm({...editForm, title: e.target.value})} />
                <div style={{ display: "flex", gap: "10px", marginTop: "5px" }}>
                  <input className="emp-input" value={editForm.city} onChange={e => setEditForm({...editForm, city: e.target.value})} />
                  <button className="emp-btn-primary" onClick={saveJobUpdate}>Save</button>
                  <button className="emp-btn-ghost" onClick={() => setIsEditing(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <div className="va-hdr-title">{jobDetail?.title} <button onClick={startEdit} className="va-edit-link">✎ Edit</button></div>
                <div className="va-hdr-meta">
                  {jobDetail?.city} · {jobDetail?.jobType} · Posted {new Date(jobDetail?.createdAt).toLocaleDateString()}
                </div>
              </>
            )
          }
        </div>
        <div className="va-hdr-stats">
          {[
            [jobDetail?.applicationCount, "Total"],
            [candidates.filter(c => c.status === "shortlisted").length, "Shortlisted"],
            [jobDetail?.viewCount, "Views"]
          ].map(([n, l]) => (
            <div key={l} className="va-hdr-stat">
              <div className="va-hdr-stat-n">{n}</div>
              <div className="va-hdr-stat-l">{l}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="va-toolbar">
        {FILTERS.map(f => (
          <button key={f} className={`va-filter-btn${filter === f ? " active" : ""}`} onClick={() => setFilter(f)}>
            {f} {f !== "All" && `(${candidates.filter(c => c.status.toLowerCase() === f.toLowerCase()).length})`}
          </button>
        ))}
      </div>

      <div className="va-list">
        {filtered.map(c => (
          <div key={c.id} className="va-card">
            {/* Initials from Name */}
            <div className="va-avatar">{c.applicantName.split(' ').map(n => n[0]).join('')}</div>
            <div className="va-info">
              <div className="va-name">{c.applicantName}</div>
              <div className="va-spec">{c.applicantSpec || "Beauty Professional"}</div>
              <div className="va-tags">
                {/* Status Pill */}
                <span className={pillMap[c.status.toLowerCase()]}>{c.status}</span>
                {c.matchScore && <span className="emp-tag emp-tag-match">Match: {c.matchScore}%</span>}
              </div>
            </div>
            <div className="va-date">{new Date(c.appliedAt).toLocaleDateString()}</div>
            <div className="va-actions">
              <button className="va-btn view" onClick={() => navigate(`/employer/candidate/${c.applicantId}`)}>View Profile</button>
              
              <button 
                className={`va-btn shortlist${c.status === "shortlisted" ? " on" : ""}`} 
                onClick={() => updateStatus(c.id, c.status === "shortlisted" ? "new" : "shortlisted")}
              >
                {c.status === "shortlisted" ? "♥ Shortlisted" : "♡ Shortlist"}
              </button>

              <button 
                className={`va-btn${c.status === "rejected" ? " rejected" : " reject"}`} 
                onClick={() => updateStatus(c.id, c.status === "rejected" ? "new" : "rejected")}
              >
                {c.status === "rejected" ? "Rejected" : "Pass"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </EmpLayout>
  );
}